import React, { useEffect } from 'react';
import { Phone, PhoneOff, Video, Loader2 } from 'lucide-react';
import Avatar from '../Avatar';
import { useCall } from '../../contexts/CallContext';
import { startRingtone, stopRingtone } from '../../utils/ringtone';

/**
 * Full-screen incoming call overlay with Accept / Decline.
 */
const IncomingCallModal = () => {
  const { callState, callType, peer, acceptCall, rejectCall } = useCall();

  useEffect(() => {
    if (callState !== 'incoming') return undefined;
    startRingtone();
    return () => stopRingtone();
  }, [callState]);

  if (callState !== 'incoming' && callState !== 'accepting') return null;

  const name = peer?.name || 'Incoming call';
  const isVideo = callType === 'video';
  const isAccepting = callState === 'accepting';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-secondary-950/90 backdrop-blur-sm p-4"
      data-testid="incoming-call-modal"
      role="dialog"
      aria-modal="true"
      aria-label={isAccepting ? 'Connecting call' : 'Incoming call'}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-secondary-900 shadow-2xl border border-secondary-200 dark:border-secondary-700 p-8 text-center">
        <div className="mx-auto mb-4 relative inline-flex">
          {!isAccepting && (
            <div className="absolute inset-0 rounded-full bg-primary-400/30 animate-ping" />
          )}
          <Avatar
            blobName={peer?.avatar}
            alt={name}
            size="2xl"
            className="relative"
            fallbackText={name}
          />
        </div>
        <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-1">
          {name}
        </h2>
        <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-8 inline-flex items-center justify-center gap-1.5">
          {isAccepting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Connecting…
            </>
          ) : (
            <>
              {isVideo ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
              Incoming {isVideo ? 'video' : 'voice'} call…
            </>
          )}
        </p>

        <div className="flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={rejectCall}
            className="flex flex-col items-center gap-2"
            data-testid="decline-call"
            aria-label={isAccepting ? 'Cancel call' : 'Decline call'}
          >
            <span className="h-14 w-14 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 shadow-lg">
              <PhoneOff className="h-6 w-6" />
            </span>
            <span className="text-xs text-secondary-600 dark:text-secondary-300">
              {isAccepting ? 'Cancel' : 'Decline'}
            </span>
          </button>
          <button
            type="button"
            onClick={acceptCall}
            disabled={isAccepting}
            className="flex flex-col items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="accept-call"
            aria-label="Accept call"
          >
            <span
              className={`h-14 w-14 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 shadow-lg ${
                isAccepting ? '' : 'animate-pulse'
              }`}
            >
              {isAccepting ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Phone className="h-6 w-6" />
              )}
            </span>
            <span className="text-xs text-secondary-600 dark:text-secondary-300">
              {isAccepting ? 'Connecting' : 'Accept'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;
